import { useState, useEffect, useRef } from 'react';

interface ScenarioSelectorProps {
  onFilterChange: (filters: {
    industry: string;
    mode: string;
    goal: string;
  }) => void;
}

export default function ScenarioSelector({ onFilterChange }: ScenarioSelectorProps) {
  const [industry, setIndustry] = useState('');
  const [mode, setMode] = useState('');
  const [goal, setGoal] = useState('');
  const isMounted = useRef(false);

  const industries = [
    { value: 'ecommerce', label: '跨境电商' },
    { value: 'trade', label: '外贸公司' },
    { value: 'market', label: '市场商户' },
    { value: 'mixed', label: '混合业务' }
  ];

  const modes = [
    { value: 'boutique', label: '精品店' },
    { value: 'bulk', label: '铺货店群' },
    { value: 'initial', label: '初期验证' },
    { value: 'mixed', label: '混合' }
  ];

  const goals = [
    { value: 'tax-free', label: '免税合规' },
    { value: 'tax-refund', label: '退税推进' },
    { value: 'pre-tax', label: '预退税与核销管理' },
    { value: 'accounting', label: '账务清晰' },
    { value: 'support', label: '高峰保障' }
  ];

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (typeof onFilterChange === 'function') {
      onFilterChange({ industry, mode, goal });
    }
  }, [industry, mode, goal, onFilterChange]);

  const handleIndustryChange = (value: string) => {
    setIndustry(value);
    // 如果不是跨境电商，清空模式选择
    if (value !== 'ecommerce') {
      setMode('');
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8">
      <div className="max-w-5xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          快速匹配方案
        </h3>
        
        <div className="space-y-6">
          {/* 行业选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              行业
            </label>
            <div className="flex flex-wrap gap-3">
              {industries.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleIndustryChange(item.value)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    industry === item.value
                      ? 'bg-teal-500 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* 模式选择（仅跨境电商显示） */}
          {industry === 'ecommerce' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                模式
              </label>
              <div className="flex flex-wrap gap-3">
                {modes.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setMode(item.value)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      mode === item.value
                        ? 'bg-teal-500 text-white shadow-md scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 目标选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              目标
            </label>
            <div className="flex flex-wrap gap-3">
              {goals.map((item) => (
                <button
                  key={item.value}
                  onClick={() => setGoal(item.value)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    goal === item.value
                      ? 'bg-teal-500 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 已选择的筛选条件提示 */}
        {(industry || mode || goal) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-filter-3-line"></i>
                <span>已选择：</span>
                {industry && (
                  <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full">
                    {industries.find(i => i.value === industry)?.label}
                  </span>
                )}
                {mode && (
                  <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full">
                    {modes.find(m => m.value === mode)?.label}
                  </span>
                )}
                {goal && (
                  <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full">
                    {goals.find(g => g.value === goal)?.label}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setIndustry('');
                  setMode('');
                  setGoal('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                清除筛选
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}